import { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import DirectorPortal from './DirectorPortal';
import JudgePortal from './JudgePortal';
import ContestantDashboard from './ContestantDashboard';

const GOLD = '#C9A84C';
const GOLD_LIGHT = '#E8C96A';
const GOLD_BORDER = 'rgba(201,168,76,0.2)';
const CREAM = '#FDFBF7';
const CHARCOAL = '#1C1C1E';
const MUTED = '#8A8070';
const WHITE = '#FFFFFF';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Jost:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Jost', sans-serif; background: ${CREAM}; }
  .auth-input { width: 100%; padding: 14px 16px; border: 1px solid ${GOLD_BORDER}; border-radius: 10px; background: white; font-family: 'Jost', sans-serif; font-size: 14px; color: ${CHARCOAL}; outline: none; transition: border 0.2s; margin-bottom: 14px; }
  .auth-input:focus { border-color: ${GOLD}; box-shadow: 0 0 0 3px rgba(201,168,76,0.1); }
  .auth-input::placeholder { color: #C4B8A8; }
  .gold-btn { transition: all 0.2s; }
  .gold-btn:hover { opacity: 0.88; transform: translateY(-1px); }
  .role-card { transition: all 0.2s; cursor: pointer; }
  .role-card:hover { transform: translateY(-3px); box-shadow: 0 12px 32px rgba(201,168,76,0.2) !important; border-color: ${GOLD} !important; }
`;

// ─── Loading Screen ───────────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{ minHeight: '100vh', background: CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '56px', height: '56px', background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, borderRadius: '14px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', marginBottom: '16px', boxShadow: `0 8px 24px rgba(201,168,76,0.3)` }}>♛</div>
        <p style={{ color: GOLD, fontSize: '12px', letterSpacing: '0.15em', textTransform: 'uppercase' }}>Loading Scorenity...</p>
      </div>
    </div>
  );
}

// ─── Role Selector ────────────────────────────────────────────────────────────
function RoleSelector({ roles, onSelect, onSignOut, userName }) {
  const roleConfig = {
    director:   { icon: '👑', label: 'Director', desc: 'Manage pageants, judges, and results', color: GOLD },
    judge:      { icon: '⚖️', label: 'Judge', desc: 'Score contestants in active pageants', color: '#6366F1' },
    contestant: { icon: '🌟', label: 'Contestant', desc: 'View your scores and pageant history', color: '#10B981' },
    auditor:    { icon: '🔍', label: 'Auditor', desc: 'Review scoring records and audit logs', color: '#F59E0B' },
  };

  return (
    <>
      <style>{styles}</style>
      <div style={{ minHeight: '100vh', background: CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,168,76,0.1), transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-80px', left: '-80px', width: '280px', height: '280px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,168,76,0.07), transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ width: '100%', maxWidth: '480px', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: '36px' }}>
            <div style={{ width: '60px', height: '60px', background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, borderRadius: '15px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', marginBottom: '16px', boxShadow: `0 8px 24px rgba(201,168,76,0.35)` }}>♛</div>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', fontWeight: '600', color: CHARCOAL, marginBottom: '6px' }}>Welcome back{userName ? `, ${userName.split(' ')[0]}` : ''}</h1>
            <p style={{ fontSize: '13px', color: MUTED }}>You have multiple roles. How would you like to sign in today?</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
            {roles.map(role => {
              const config = roleConfig[role] || { icon: '◈', label: role, desc: '', color: GOLD };
              return (
                <div key={role} className="role-card" onClick={() => onSelect(role)} style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '14px', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: `${config.color}18`, border: `1px solid ${config.color}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>{config.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: CHARCOAL, fontWeight: '600', lineHeight: 1, marginBottom: '4px' }}>{config.label}</div>
                    <div style={{ fontSize: '12px', color: MUTED }}>{config.desc}</div>
                  </div>
                  <div style={{ fontSize: '18px', color: GOLD_BORDER }}>→</div>
                </div>
              );
            })}
          </div>
          <button onClick={onSignOut} style={{ width: '100%', background: 'none', border: `1px solid ${GOLD_BORDER}`, borderRadius: '10px', padding: '12px', fontSize: '12px', color: MUTED, cursor: 'pointer', fontFamily: "'Jost', sans-serif", letterSpacing: '0.06em' }}>Sign Out</button>
          <p style={{ textAlign: 'center', fontSize: '11px', color: MUTED, marginTop: '20px', letterSpacing: '0.04em' }}>People · Business · Growth · Excellence</p>
        </div>
      </div>
    </>
  );
}

// ─── Login Page ───────────────────────────────────────────────────────────────
function LoginPage() {
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  async function handleSignIn(e) {
    e.preventDefault();
    setLoading(true); setError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(error.message);
    setLoading(false);
  }

  async function handleSignUp(e) {
    e.preventDefault();
    setLoading(true); setError('');
    const { error } = await supabase.auth.signUp({ email, password, options: { data: { full_name: fullName } } });
    if (error) setError(error.message);
    else setMessage('Account created! Check your email to confirm, then sign in.');
    setLoading(false);
  }

  return (
    <div style={{ minHeight: '100vh', background: CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: '-100px', right: '-100px', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,168,76,0.12), transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '-100px', left: '-100px', width: '350px', height: '350px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,168,76,0.08), transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ background: WHITE, border: '1px solid rgba(201,168,76,0.2)', borderRadius: '20px', padding: '44px 40px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(201,168,76,0.12), 0 4px 20px rgba(0,0,0,0.06)', position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{ width: '64px', height: '64px', background: 'linear-gradient(135deg, #C9A84C, #E8C96A)', borderRadius: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', marginBottom: '16px', boxShadow: '0 8px 24px rgba(201,168,76,0.3)' }}>♛</div>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', fontWeight: '600', color: CHARCOAL, letterSpacing: '-0.01em', lineHeight: 1, marginBottom: '6px' }}>Scorenity</h1>
          <p style={{ fontSize: '11px', color: GOLD, letterSpacing: '0.2em', textTransform: 'uppercase' }}>{mode === 'signin' ? 'Welcome Back' : 'Create Account'}</p>
        </div>
        {error && <div style={{ background: '#FFF0F0', border: '1px solid #FFCCCC', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#C0392B', marginBottom: '14px', textAlign: 'center' }}>{error}</div>}
        {message && <div style={{ background: '#F0FFF4', border: '1px solid #C6F6D5', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#276749', marginBottom: '14px', textAlign: 'center' }}>{message}</div>}
        <form onSubmit={mode === 'signin' ? handleSignIn : handleSignUp}>
          {mode === 'signup' && <input className="auth-input" type="text" placeholder="Full Name" value={fullName} onChange={e => setFullName(e.target.value)} required />}
          <input className="auth-input" type="email" placeholder="Email Address" value={email} onChange={e => setEmail(e.target.value)} required />
          <input className="auth-input" type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required />
          <button type="submit" disabled={loading} style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, #C9A84C, #E8C96A)', border: 'none', borderRadius: '10px', fontFamily: "'Jost', sans-serif", fontSize: '14px', fontWeight: '600', color: WHITE, letterSpacing: '0.08em', cursor: loading ? 'not-allowed' : 'pointer', marginTop: '4px', transition: 'all 0.2s' }}>
            {loading ? 'Please wait...' : mode === 'signin' ? 'Sign In' : 'Create Account'}
          </button>
        </form>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0' }}>
          <div style={{ flex: 1, height: '1px', background: 'rgba(201,168,76,0.2)' }} />
          <span style={{ fontSize: '11px', color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase' }}>or</span>
          <div style={{ flex: 1, height: '1px', background: 'rgba(201,168,76,0.2)' }} />
        </div>
        <button onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(''); setMessage(''); }} style={{ width: '100%', padding: '14px', background: 'transparent', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '10px', fontFamily: "'Jost', sans-serif", fontSize: '14px', color: GOLD, letterSpacing: '0.06em', cursor: 'pointer', transition: 'all 0.2s' }}>
          {mode === 'signin' ? 'Create a new account' : 'Already have an account? Sign in'}
        </button>
        <p style={{ textAlign: 'center', fontSize: '11px', color: MUTED, marginTop: '24px', letterSpacing: '0.04em' }}>People · Business · Growth · Excellence</p>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [session, setSession] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) fetchUserRoles(session.user.id);
      else setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) fetchUserRoles(session.user.id);
      else { setUserRoles([]); setSelectedRole(null); setLoading(false); }
    });
    return () => subscription.unsubscribe();
  }, []);

  async function fetchUserRoles(userId) {
    setLoading(true);

    // Ensure user exists in public.users table
    const { data: authUser } = await supabase.auth.getUser();
    if (authUser?.user) {
      await supabase.from('users').upsert({
        id: authUser.user.id,
        email: authUser.user.email,
        full_name: authUser.user.user_metadata?.full_name || authUser.user.email.split('@')[0],
      }, { onConflict: 'id', ignoreDuplicates: true });
    }

    const [rolesRes, userRes] = await Promise.all([
      supabase.from('org_members').select('role').eq('user_id', userId).eq('is_active', true),
      supabase.from('users').select('full_name').eq('id', userId).single(),
    ]);
    const roles = [...new Set((rolesRes.data || []).map(r => r.role))];
    setUserRoles(roles);
    setUserName(userRes.data?.full_name || '');
    if (roles.length === 1) setSelectedRole(roles[0]);
    else setSelectedRole(null);
    setLoading(false);
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    setSelectedRole(null);
    setUserRoles([]);
  }

  if (loading) return <LoadingScreen />;

  if (!session) return (
    <>
      <style>{styles}</style>
      <LoginPage />
    </>
  );

  if (userRoles.length > 1 && !selectedRole) {
    return <RoleSelector roles={userRoles} userName={userName} onSelect={setSelectedRole} onSignOut={handleSignOut} />;
  }

  if (userRoles.length === 0) {
    return (
      <>
        <style>{styles}</style>
        <div style={{ minHeight: '100vh', background: CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ textAlign: 'center', maxWidth: '360px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>♛</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '24px', color: CHARCOAL, marginBottom: '8px' }}>Account Pending</h2>
            <p style={{ color: MUTED, fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>Your account has been created but you haven't been assigned to any organization yet. Contact your pageant director to be added.</p>
            <button onClick={handleSignOut} style={{ background: 'none', border: `1px solid ${GOLD_BORDER}`, borderRadius: '10px', padding: '12px 24px', color: GOLD, fontFamily: "'Jost', sans-serif", fontSize: '13px', cursor: 'pointer' }}>Sign Out</button>
          </div>
        </div>
      </>
    );
  }

  const role = selectedRole || userRoles[0];

  return (
    <>
      <style>{styles}</style>
      {role === 'director' ? (
        <DirectorPortal user={session.user} onSignOut={handleSignOut} onSwitchRole={userRoles.length > 1 ? () => setSelectedRole(null) : null} />
      ) : role === 'judge' ? (
        <JudgePortal user={session.user} onSignOut={handleSignOut} onSwitchRole={userRoles.length > 1 ? () => setSelectedRole(null) : null} />
      ) : (
        <ContestantDashboard user={session.user} onSignOut={handleSignOut} onSwitchRole={userRoles.length > 1 ? () => setSelectedRole(null) : null} />
      )}
    </>
  );
}